import { Injectable, inject } from '@angular/core';
import { DocumentDialogComponent } from '@components/dialog/document-dialog/document-dialog.component';
import { DocumentSaveService, DocumentService, ModifiedFieldService, UnsavedDocumentService } from '@formbird/services';
import { SharedConstants } from '@formbird/types';
import * as lodashLang from 'lodash/lang';
import { ModalService } from './modal.service';
import { ConfirmSaveChangesService } from './confirm-save-change.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentModalService {
  private unsavedDocumentService = inject(UnsavedDocumentService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private confirmSaveChangesService = inject(ConfirmSaveChangesService);
  private documentSaveService = inject(DocumentSaveService);
  private documentService = inject(DocumentService);
  private modalService = inject(ModalService);

  private documentModel = {};

  getDocumentModel() {
    return this.documentModel;
  }

  setDocumentModel(model) {
    this.documentModel = model;
  }

  async openDocumentInModal(documentId, options): Promise<any> {
    await this.documentService.getDocumentOnly(documentId);
  
    return new Promise((resolve, reject) => {
      let unsavedDocumentListId;
      let modalTitle;
  
      const formParameters: any = {};
      if (options) {
        formParameters.parentDocumentId = options.parentDocumentId;
        formParameters.defaultFields = options.defaultFields;
        formParameters.hierarchyInfo = options.hierarchyInfo;
        unsavedDocumentListId = options.unsavedDocumentListId;
        modalTitle = options.modalTitle;
      }
  
      if (!unsavedDocumentListId) {
        unsavedDocumentListId = this.unsavedDocumentService.createUnsavedDocumentList();
      }
  
      formParameters.unsavedDocumentListId = unsavedDocumentListId;
      formParameters.selectedDocumentId = documentId;
      formParameters.isModal = true;

      const getUnsavedDocumentId = () => {
        if (!lodashLang.isEmpty(unsavedDocumentListId)) {
          const unsavedDocuments = this.unsavedDocumentService.getUnsavedDocuments(unsavedDocumentListId);
          const firstKey = Object.keys(unsavedDocuments || {})[0];
          if (firstKey && unsavedDocuments[firstKey]) {
            return unsavedDocuments[firstKey].documentId;
          }
        }
        return null;
      };

      const cancelFunc = () => {
        const docId = getUnsavedDocumentId();
        if (docId) {
          this.modifiedFieldService.removeModifiedDocumentFields(docId);
        }
        resolve(null);
      };

      const discardFunc = () => {
        cancelFunc();
        this.modalService.closeDialog();
      };

      const saveFunc = async () => {
        try {
          const result = await this.documentSaveService.save(unsavedDocumentListId);
          this.modalService.closeDialog();
          resolve(result);
        } catch (err) {
          this.modalService.showDialog(err.message, 'ERROR');
          reject(err);
        }
      };

      const closeFunc = async () => {
        const docId = getUnsavedDocumentId();
        const hasUnsavedChanges = docId && this.modifiedFieldService.getModifiedFieldsInDoc(docId)?.length > 0;

        if (hasUnsavedChanges) {
          await this.confirmSaveChangesService.confirm({
            onSuccess: saveFunc,
            onDiscard: discardFunc,
            onCancel: cancelFunc
          });
        } else {
          discardFunc();
        }
      };
      const data = {
        component: DocumentDialogComponent,
        html: `
          <div class="divTemplateDialog">
              <div class="ft-modal-header">
                  <button type="button" class="modelClose" data-dismiss="modal" aria-hidden="true" ng-click="saveModal('cancel')">
                      <i class="material-icons">close</i></button>
                  <h4 class="modal-title">
                      <button type="button" ng-show="formParameters.dirtyClass==='show'?true:false"  class="modelSave"
                              data-dismiss="modal" aria-hidden="true" ng-click="saveModal('save')">
                          <i class="material-icons blink {{formParameters.dirtyClass}}">check</i></button>
                      {{modalTitle}}
                  </h4>
              </div>
              <div class="ft-modal-body"></div>
              <div class="ft-modal-footer"></div>
          </div>`,
        messageType: SharedConstants.STATUS_SUCCESS,
        confirmButton: true,
        showCancelButton: true,
        allowEnterKey: false,
        title: modalTitle,
        templateId: documentId,
        formParameters: formParameters,
        succFunc: saveFunc,
        errorFunc: closeFunc
      };

      this.modalService.openDialog({
        data,
        height: 'max-content',
        width: '80vw',
        panelClass: 'ft-document-dialog'
      });
    });
  }
}
